// The typed wire contract of all 22 synced collections (WEB_PLAN §2.2-2.3). One module per collection; this file ties
// them to `Collection` so sync/ and domain/ code can decode any row generically.
//
// Reminder of the rules every module follows (WEB_PLAN §0): keys are the Swift property names, payload dates are
// whole-second UTC `Z`, ids are upper-case when written and compared with `sameId`, nil optionals are omitted keys,
// enums are raw strings, Ints are integral, and edits are patches on the raw server payload (the decoded value is a
// typed view for display and logic, never the thing that is pushed).

import type { Collection } from '../collections';
import type { JsonObject } from '../wire';
import { optionalKeysOf, type Decoded, type FieldMap } from './fields';

import { ACTIVITY_FIELDS, ACTIVITY_KEYS, decodeActivity, type ActivityPayload } from './activities';
import { ATTACHMENT_FIELDS, ATTACHMENT_KEYS, decodeAttachment, type AttachmentPayload } from './attachments';
import { AUTOPLAY_FIELDS, AUTOPLAY_KEYS, decodeAutoplay, type AutoplayPayload } from './autoplays';
import { BADGE_FIELDS, BADGE_KEYS, decodeBadge, type BadgePayload } from './badges';
import { COMM_FIELDS, COMM_KEYS, decodeComm, type CommPayload } from './comms';
import { CUSTOM_FIELD_FIELDS, CUSTOM_FIELD_KEYS, decodeCustomField, type CustomFieldPayload } from './customFields';
import { DAY_LOG_FIELDS, DAY_LOG_KEYS, decodeDayLog, type DayLogPayload } from './dayLogs';
import { DOCUMENT_FIELDS, DOCUMENT_KEYS, decodeDocument, type DocumentPayload } from './documents';
import { ENROLLMENT_FIELDS, ENROLLMENT_KEYS, decodeEnrollment, type EnrollmentPayload } from './enrollments';
import { GOAL_FIELDS, GOAL_KEYS, decodeGoal, type GoalPayload } from './goals';
import { INTEGRATION_FIELDS, INTEGRATION_KEYS, decodeIntegration, type IntegrationPayload } from './integrations';
import { LEAD_SOURCE_FIELDS, LEAD_SOURCE_KEYS, decodeLeadSource, type LeadSourcePayload } from './leadSources';
import { LINK_FIELDS, LINK_KEYS, decodeLink, type LinkPayload } from './links';
import { PIPELINE_FIELDS, PIPELINE_KEYS, decodePipeline, type PipelinePayload } from './pipelines';
import {
  RECORD_FIELDS,
  RECORD_KEYS,
  RECORD_LOCAL_ONLY_KEYS,
  decodeRecord,
  type RecordPayload,
} from './records';
import { ROUTE_FIELDS, ROUTE_KEYS, decodeRoute, type RoutePayload } from './routes';
import { SETTINGS_FIELDS, SETTINGS_KEYS, decodeSettings, type SettingsPayload } from './settings';
import { TEAM_FIELDS, TEAM_KEYS, decodeTeam, type TeamPayload } from './teams';
import { TEMPLATE_FIELDS, TEMPLATE_KEYS, decodeTemplate, type TemplatePayload } from './templates';
import { TERRITORY_FIELDS, TERRITORY_KEYS, decodeTerritory, type TerritoryPayload } from './territories';
import { USER_FIELDS, USER_KEYS, USER_LOCAL_ONLY_KEYS, decodeUser, type UserPayload } from './users';
import { VISIT_RESULT_FIELDS, VISIT_RESULT_KEYS, decodeVisitResult, type VisitResultPayload } from './visitResults';

export * from './activities';
export * from './attachments';
export * from './autoplays';
export * from './badges';
export * from './comms';
export * from './common';
export * from './customFields';
export * from './dayLogs';
export * from './documents';
export * from './enrollments';
export * from './goals';
export * from './integrations';
export * from './leadSources';
export * from './links';
export * from './pipelines';
export * from './records';
export * from './routes';
export * from './settings';
export * from './teams';
export * from './templates';
export * from './territories';
export * from './users';
export * from './visitResults';
export { EPOCH, isIosDecodableDate, isWireDate, nowWire } from './fields';
export type { Decoded, Known, Passthrough } from './fields';

/** Payload type of each collection. */
export interface PayloadMap {
  settings: SettingsPayload;
  users: UserPayload;
  teams: TeamPayload;
  pipelines: PipelinePayload;
  visitResults: VisitResultPayload;
  customFields: CustomFieldPayload;
  leadSources: LeadSourcePayload;
  templates: TemplatePayload;
  badges: BadgePayload;
  territories: TerritoryPayload;
  records: RecordPayload;
  activities: ActivityPayload;
  attachments: AttachmentPayload;
  routes: RoutePayload;
  autoplays: AutoplayPayload;
  enrollments: EnrollmentPayload;
  comms: CommPayload;
  dayLogs: DayLogPayload;
  goals: GoalPayload;
  integrations: IntegrationPayload;
  links: LinkPayload;
  documents: DocumentPayload;
}

export type PayloadOf<C extends Collection> = PayloadMap[C];

/** What generic code needs to know about one collection's payloads. */
export interface ModelSpec<T> {
  /** Wire keys = Swift stored properties in declaration order, minus local-only keys (`X_KEYS`). */
  readonly keys: readonly string[];
  /** The keys whose Swift property is Optional (omitted when nil, never `null`). */
  readonly optionalKeys: readonly string[];
  /** Keys iOS never puts on the wire for this collection (records, users); strip before push, ignore on read. */
  readonly localOnlyKeys: readonly string[];
  /** False only for `settings`, whose payload has no `id` (envelope id SETTINGS_ID). */
  readonly hasId: boolean;
  /**
   * Lenient decode like ResilientDecoding. With `envelopeId`, a payload `id` that differs from it is undecodable too
   * (iOS drops such items: SyncReplica.merge requires `v.id == name.id`).
   */
  decode(raw: JsonObject, envelopeId?: string): Decoded<T>;
}

function spec<T>(
  keys: readonly string[],
  fields: FieldMap<T>,
  decode: (raw: JsonObject, envelopeId?: string) => Decoded<T>,
  extra: { localOnlyKeys?: readonly string[]; hasId?: boolean } = {},
): ModelSpec<T> {
  return {
    keys,
    optionalKeys: optionalKeysOf(fields),
    localOnlyKeys: extra.localOnlyKeys ?? [],
    hasId: extra.hasId ?? true,
    decode,
  };
}

/** Per-collection registry: wire keys, optional/local-only keys and the decoder. */
export const MODELS: { readonly [C in Collection]: ModelSpec<PayloadOf<C>> } = {
  settings: spec(SETTINGS_KEYS, SETTINGS_FIELDS, (raw) => decodeSettings(raw), { hasId: false }),
  users: spec(USER_KEYS, USER_FIELDS, decodeUser, { localOnlyKeys: USER_LOCAL_ONLY_KEYS }),
  teams: spec(TEAM_KEYS, TEAM_FIELDS, decodeTeam),
  pipelines: spec(PIPELINE_KEYS, PIPELINE_FIELDS, decodePipeline),
  visitResults: spec(VISIT_RESULT_KEYS, VISIT_RESULT_FIELDS, decodeVisitResult),
  customFields: spec(CUSTOM_FIELD_KEYS, CUSTOM_FIELD_FIELDS, decodeCustomField),
  leadSources: spec(LEAD_SOURCE_KEYS, LEAD_SOURCE_FIELDS, decodeLeadSource),
  templates: spec(TEMPLATE_KEYS, TEMPLATE_FIELDS, decodeTemplate),
  badges: spec(BADGE_KEYS, BADGE_FIELDS, decodeBadge),
  territories: spec(TERRITORY_KEYS, TERRITORY_FIELDS, decodeTerritory),
  records: spec(RECORD_KEYS, RECORD_FIELDS, decodeRecord, { localOnlyKeys: RECORD_LOCAL_ONLY_KEYS }),
  activities: spec(ACTIVITY_KEYS, ACTIVITY_FIELDS, decodeActivity),
  attachments: spec(ATTACHMENT_KEYS, ATTACHMENT_FIELDS, decodeAttachment),
  routes: spec(ROUTE_KEYS, ROUTE_FIELDS, decodeRoute),
  autoplays: spec(AUTOPLAY_KEYS, AUTOPLAY_FIELDS, decodeAutoplay),
  enrollments: spec(ENROLLMENT_KEYS, ENROLLMENT_FIELDS, decodeEnrollment),
  comms: spec(COMM_KEYS, COMM_FIELDS, decodeComm),
  dayLogs: spec(DAY_LOG_KEYS, DAY_LOG_FIELDS, decodeDayLog),
  goals: spec(GOAL_KEYS, GOAL_FIELDS, decodeGoal),
  integrations: spec(INTEGRATION_KEYS, INTEGRATION_FIELDS, decodeIntegration),
  links: spec(LINK_KEYS, LINK_FIELDS, decodeLink),
  documents: spec(DOCUMENT_KEYS, DOCUMENT_FIELDS, decodeDocument),
};

/** Decodes any collection's payload (see ModelSpec.decode). */
export function decodePayload<C extends Collection>(
  collection: C,
  raw: JsonObject,
  envelopeId?: string,
): Decoded<PayloadOf<C>> {
  return (MODELS[collection] as ModelSpec<PayloadOf<C>>).decode(raw, envelopeId);
}
